import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  HiPlus as Add,
  HiClipboardList as Assignment,
  HiCalendar as Calendar,
  HiClock as Clock,
  HiTrash as Delete,
  HiPencil as Edit,
  HiChevronDown as ExpandMore,
  HiUsers as Users,
  HiEye as Visibility,
} from 'react-icons/hi';
import { mockData } from './MockData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`teachers-tabpanel-${index}`}
      aria-labelledby={`teachers-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TeachersManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [_selectedTeacher, _setSelectedTeacher] = useState<any>(null);
  const [newTeacher, setNewTeacher] = useState<any>({});
  const [newAssignment, setNewAssignment] = useState<any>({});
  const [teachers, setTeachers] = useState(mockData.teachers);
  const [assignments, setAssignments] = useState(mockData.assignments);
  const [workloadData, _setWorkloadData] = useState(mockData.workloadSummary);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddTeacher = () => {
    const teacherWithId = { ...newTeacher, id: Date.now().toString() };
    setTeachers([...teachers, teacherWithId]);
    setNewTeacher({});
    setShowAddDialog(false);
  };

  const handleAddAssignment = () => {
    const assignmentWithId = { ...newAssignment, id: Date.now().toString() };
    setAssignments([...assignments, assignmentWithId]);
    setNewAssignment({});
    setShowAssignmentDialog(false);
  };

  const getWorkloadColor = (utilization: number) => {
    if (utilization < 70) return 'success';
    if (utilization < 90) return 'warning';
    return 'error';
  };

  const renderTeacherProfiles = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Teacher Profiles</Typography>
          <Button startIcon={<Add />} onClick={() => setShowAddDialog(true)}>
            Add Teacher
          </Button>
        </Box>
        <Grid container spacing={2}>
          {teachers.map((teacher) => (
            <Grid item xs={12} sm={6} md={4} key={teacher.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      {teacher.personalInfo.firstName.charAt(0)}
                      {teacher.personalInfo.lastName.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1">
                        {teacher.personalInfo.firstName} {teacher.personalInfo.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {teacher.roles.primaryRole}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Contract:
                    </Typography>
                    <Chip
                      label={teacher.employment.contractType}
                      color={teacher.employment.contractType === 'full-time' ? 'primary' : 'secondary'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Teaching Hours:
                    </Typography>
                    <Typography variant="body2">{teacher.employment.teachingCapacity} periods/week</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Subjects:
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {teacher.qualifications.slice(0, 2).map((qual, idx) => (
                        <Chip key={idx} label={qual.subjectName} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                      {teacher.qualifications.length > 2 && (
                        <Chip label={`+${teacher.qualifications.length - 2}`} size="small" />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    {teacher.roles.isHOD && <Chip label="HOD" color="secondary" size="small" />}
                    {teacher.roles.isSLT && <Chip label="SLT" color="secondary" size="small" />}
                    <Box>
                      <IconButton size="small" color="primary">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small" color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  const renderAssignmentMatrix = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Teacher-Subject Assignments</Typography>
          <Button startIcon={<Assignment />} onClick={() => setShowAssignmentDialog(true)}>
            Add Assignment
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Teacher</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Year Group</TableCell>
                <TableCell>Assignment Type</TableCell>
                <TableCell>Periods/Week</TableCell>
                <TableCell>Room</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 1 }}>{assignment.teacherName.charAt(0)}</Avatar>
                      {assignment.teacherName}
                    </Box>
                  </TableCell>
                  <TableCell>{assignment.subjectName}</TableCell>
                  <TableCell>{assignment.yearGroup}</TableCell>
                  <TableCell>
                    <Chip
                      label={assignment.assignmentType}
                      color={assignment.assignmentType === 'primary' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{assignment.periodsPerWeek}</TableCell>
                  <TableCell>{assignment.roomName || 'Not assigned'}</TableCell>
                  <TableCell>
                    <IconButton size="small" color="primary">
                      <Edit />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );

  const renderWorkloadTracking = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Teacher Workload Distribution
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Teacher</TableCell>
                    <TableCell>Assigned</TableCell>
                    <TableCell>Contractual</TableCell>
                    <TableCell>Utilization</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workloadData.map((workload) => (
                    <TableRow key={workload.teacherId}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 32, height: 32, mr: 1 }}>{workload.teacherName.charAt(0)}</Avatar>
                          {workload.teacherName}
                        </Box>
                      </TableCell>
                      <TableCell>{workload.totalAssignedPeriods}</TableCell>
                      <TableCell>{workload.contractualPeriods}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={workload.utilizationPercentage}
                              color={getWorkloadColor(workload.utilizationPercentage)}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {workload.utilizationPercentage}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={workload.status}
                          color={getWorkloadColor(workload.utilizationPercentage)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary">
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Workload Summary
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Paper sx={{ p: 2, textAlign: 'center', mb: 1, bgcolor: 'success.50' }}>
                <Typography variant="h4" color="success.main">
                  {workloadData.filter((w) => w.status === 'optimal').length}
                </Typography>
                <Typography variant="body2">Optimal Load</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center', mb: 1, bgcolor: 'warning.50' }}>
                <Typography variant="h4" color="warning.main">
                  {workloadData.filter((w) => w.status === 'underloaded').length}
                </Typography>
                <Typography variant="body2">Under-loaded</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
                <Typography variant="h4" color="error.main">
                  {workloadData.filter((w) => w.status === 'overloaded').length}
                </Typography>
                <Typography variant="body2">Over-loaded</Typography>
              </Paper>
            </Box>
            <Alert severity="info">
              <Typography variant="body2">Aim for 80-95% utilization for optimal workload distribution.</Typography>
            </Alert>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderPreferencesAndAvailability = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Teacher Availability & Preferences
        </Typography>
        {teachers.map((teacher) => (
          <Accordion key={teacher.id} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                  {teacher.personalInfo.firstName.charAt(0)}
                  {teacher.personalInfo.lastName.charAt(0)}
                </Avatar>
                <Typography variant="subtitle1">
                  {teacher.personalInfo.firstName} {teacher.personalInfo.lastName}
                </Typography>
                <Box sx={{ ml: 'auto', mr: 2 }}>
                  <Chip
                    label={`${Object.values(teacher.availability.workingDays).filter((d) => d.available).length} days`}
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Working Days & Times
                  </Typography>
                  <List dense>
                    {Object.entries(teacher.availability.workingDays).map(([day, availability]) => (
                      <ListItem key={day}>
                        <ListItemText
                          primary={day}
                          secondary={
                            availability.available
                              ? `${availability.startTime} - ${availability.endTime}`
                              : 'Not available'
                          }
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={availability.available ? 'Available' : 'Unavailable'}
                            color={availability.available ? 'success' : 'default'}
                            size="small"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Teaching Preferences
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Preferred Subjects:
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {teacher.preferences.preferredSubjects.map((subject, idx) => (
                        <Chip key={idx} label={subject} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Preferred Year Groups:
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {teacher.preferences.preferredYearGroups.map((year, idx) => (
                        <Chip key={idx} label={year} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Workload Preference:
                    </Typography>
                    <Chip label={teacher.preferences.workloadPreference} size="small" sx={{ mt: 0.5 }} />
                  </Box>
                  <FormControlLabel
                    control={<Switch checked={teacher.preferences.coTeachingWillingness} size="small" />}
                    label="Co-teaching willing"
                  />
                  <FormControlLabel
                    control={<Switch checked={teacher.preferences.mentorshipAvailability} size="small" />}
                    label="Mentorship available"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Teachers Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage teaching staff, assignments, workload distribution, and preferences for optimal scheduling
      </Typography>

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="teachers management tabs">
            <Tab icon={<Users />} label="Teacher Profiles" />
            <Tab icon={<Assignment />} label="Subject Assignments" />
            <Tab icon={<Clock />} label="Workload Tracking" />
            <Tab icon={<Calendar />} label="Availability & Preferences" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {renderTeacherProfiles()}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderAssignmentMatrix()}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {renderWorkloadTracking()}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {renderPreferencesAndAvailability()}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Add Teacher Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Teacher</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={newTeacher.firstName || ''}
                onChange={(e) =>
                  setNewTeacher({
                    ...newTeacher,
                    personalInfo: { ...newTeacher.personalInfo, firstName: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={newTeacher.lastName || ''}
                onChange={(e) =>
                  setNewTeacher({
                    ...newTeacher,
                    personalInfo: { ...newTeacher.personalInfo, lastName: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newTeacher.email || ''}
                onChange={(e) =>
                  setNewTeacher({
                    ...newTeacher,
                    personalInfo: { ...newTeacher.personalInfo, email: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Contract Type</InputLabel>
                <Select
                  value={newTeacher.contractType || ''}
                  onChange={(e) =>
                    setNewTeacher({
                      ...newTeacher,
                      employment: { ...newTeacher.employment, contractType: e.target.value },
                    })
                  }
                >
                  <MenuItem value="full-time">Full-time</MenuItem>
                  <MenuItem value="part-time">Part-time</MenuItem>
                  <MenuItem value="supply">Supply</MenuItem>
                  <MenuItem value="temporary">Temporary</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teaching Capacity (periods/week)"
                type="number"
                value={newTeacher.teachingCapacity || ''}
                onChange={(e) =>
                  setNewTeacher({
                    ...newTeacher,
                    employment: { ...newTeacher.employment, teachingCapacity: Number.parseInt(e.target.value) },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Primary Role"
                value={newTeacher.primaryRole || ''}
                onChange={(e) =>
                  setNewTeacher({
                    ...newTeacher,
                    roles: { ...newTeacher.roles, primaryRole: e.target.value },
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTeacher}>
            Add Teacher
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Assignment Dialog */}
      <Dialog open={showAssignmentDialog} onClose={() => setShowAssignmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Subject Assignment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={teachers}
                getOptionLabel={(option) =>
                  `${option.personalInfo?.firstName || ''} ${option.personalInfo?.lastName || ''}`
                }
                renderInput={(params) => <TextField {...params} label="Teacher" />}
                onChange={(_e, value) =>
                  setNewAssignment({
                    ...newAssignment,
                    teacherId: value?.id,
                    teacherName: `${value?.personalInfo?.firstName || ''} ${value?.personalInfo?.lastName || ''}`,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subject"
                value={newAssignment.subjectName || ''}
                onChange={(e) => setNewAssignment({ ...newAssignment, subjectName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Year Group</InputLabel>
                <Select
                  value={newAssignment.yearGroup || ''}
                  onChange={(e) => setNewAssignment({ ...newAssignment, yearGroup: e.target.value })}
                >
                  <MenuItem value="Year 7">Year 7</MenuItem>
                  <MenuItem value="Year 8">Year 8</MenuItem>
                  <MenuItem value="Year 9">Year 9</MenuItem>
                  <MenuItem value="Year 10">Year 10</MenuItem>
                  <MenuItem value="Year 11">Year 11</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Assignment Type</InputLabel>
                <Select
                  value={newAssignment.assignmentType || ''}
                  onChange={(e) => setNewAssignment({ ...newAssignment, assignmentType: e.target.value })}
                >
                  <MenuItem value="primary">Primary</MenuItem>
                  <MenuItem value="secondary">Secondary</MenuItem>
                  <MenuItem value="backup">Backup</MenuItem>
                  <MenuItem value="co-teacher">Co-teacher</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Periods per Week"
                type="number"
                value={newAssignment.periodsPerWeek || ''}
                onChange={(e) =>
                  setNewAssignment({ ...newAssignment, periodsPerWeek: Number.parseInt(e.target.value) })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAssignmentDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddAssignment}>
            Add Assignment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeachersManagement;
